import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import XIcon from '../../../../../../../icons/X.icon';
import { EnquiryRoutes, ENQUIRY_CONTENT, ENQUIRY_ROUTES } from './utils';
import EnquiryListingProvider from './providers/EnquiryListing/EnquiryListing.provider';
import useHeightAnimation from 'hooks/useHeightAnimation';
import useCallbackRef from 'hooks/useCallbackRef';
import useScrollLock from 'hooks/useScrollLock';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './EnquirySlider.styles.scss';
import './EnquirySlider.styles.responsive.scss';

type EnquirySliderProps = {
  className?: string;
  listingId: string;
  onClose?: () => void;
  onOpen?: () => void;
};

export type EnquirySliderRef = {
  open?: () => void;
  close: () => void;
};

const EnquirySlider: ForwardRefRenderFunction<
  EnquirySliderRef,
  EnquirySliderProps
> = (props, ref) => {
  const { className, listingId, onClose, onOpen } = props;

  const { isSuperAdmin, currentUser } = useContext(CurrentUserContext);

  const initialRoute = currentUser
    ? ENQUIRY_ROUTES.REGISTRATION_OF_INTEREST_FORM
    : ENQUIRY_ROUTES.SIGN_UP;

  const [open, setOpen] = useState(false);
  const [enquiryRoute, setEnquiryRoute] = useState<EnquiryRoutes>(initialRoute);
  const [container, containerRef] = useCallbackRef<HTMLDivElement>();

  const { lockScroll, unlockScroll } = useScrollLock();

  const classes = classNames(
    'enquiry-slider',
    {
      'enquiry-slider--open': open,
    },
    className,
  );

  const EnquiryContent = useMemo(
    () => ENQUIRY_CONTENT[enquiryRoute],
    [enquiryRoute],
  );

  const setClosed = useCallback(() => {
    setOpen(false);
    unlockScroll();
    onClose?.();
  }, [onClose, unlockScroll]);

  const setOpened = useCallback(() => {
    setOpen(true);
    lockScroll();
    onOpen?.();
  }, [lockScroll, onOpen]);

  useImperativeHandle(ref, () => ({ open: setOpened, close: setClosed }), [
    setOpened,
    setClosed,
  ]);

  useHeightAnimation({
    element: container,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => unlockScroll(), []);

  // BOSS MODE cannot enquire
  if (isSuperAdmin) return null;

  return (
    <>
      <div className={classes}>
        <Button
          className="close-button"
          variant="icon"
          aria-label="Close"
          icon={XIcon}
          onClick={setClosed}
        />

        <div className="enquiry-slider__content" ref={containerRef}>
          <EnquiryListingProvider
            listingId={listingId}
            setClosed={setClosed}
            setEnquiryRoute={setEnquiryRoute}
          >
            <EnquiryContent />
          </EnquiryListingProvider>
        </div>
      </div>
      <div className="enquiry-slider__overlay" onClick={setClosed} />
    </>
  );
};

export default forwardRef(EnquirySlider);
